/**
 * Program Roles registry — the tenant's roster role catalog AND each role's
 * roster→access mapping, self-service. Assigning a role on a program's Roles
 * tab grants the mapped access role on that program; this is where a tenant
 * shapes that mapping to its own org chart without an engineer.
 *
 * Built-in (_system) role types are immutable, like built-in access roles;
 * the server refuses edits too. Editing is admin work (the RPCs ride the
 * admin-only org write area), so mutation affordances render for admins only.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/authContext';
import { deliveryClient } from '../../../lib/connect/clients';
import { ROLE_NAMES_KEY, useListBundles } from '../../Admin/Access/hooks';
import { ViewModeBadge } from '../../Admin/Permissions/PermissionsPage';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { businessUnitHooks } from '../Org/hooks';
import { useTenantEnumValues } from '../Programs/referenceDataHooks';
const NO_ACCESS = '__none__';
// Radix Select forbids an empty item value — this sentinel stands in for
// "no business unit" the same way NO_ACCESS stands in for "no bundle".
// Empty on the wire means the row is offered in EVERY business unit
// (migration 000076 / UpsertTenantEnumValueRequest.business_unit_id doc),
// not "unassigned" — the option label below says so explicitly.
const ALL_BUSINESS_UNITS = '__all_bu__';
function mappingOf(row) {
    try {
        const meta = row.metadataJson ? JSON.parse(row.metadataJson) : {};
        // The role_bundle_key column is the mapping of record; metadata.bundle_key
        // survives only for rows written before the column backfill (000081).
        return {
            bundleKey: row.roleBundleKey || (meta.bundle_key ?? ''),
            selfService: meta.self_service ?? false
        };
    } catch  {
        return {
            bundleKey: row.roleBundleKey ?? '',
            selfService: false
        };
    }
}
export function ProgramRoleTypesTab() {
    const { user } = useAuth();
    const isAdmin = [
        'admin',
        'internal'
    ].includes(user?.role?.toLowerCase() ?? '');
    const qc = useQueryClient();
    const tenantId = useTenantId();
    // NO business-unit argument here on purpose: this admin tab must list
    // EVERY role name across every business unit (plus unscoped ones) so a
    // tenant admin can see and manage the whole vocabulary, not just what one
    // program's scope would narrow to. See useTenantEnumValues' own doc for the
    // NULL-or-match semantics a scoped caller (e.g. ProgramRolesTab) relies on.
    const roleTypes = useTenantEnumValues('role_type');
    // Bundle names for the Access column + picker — admin-gated RPC, matching
    // who may edit this registry. Non-admins get the read-only table with keys.
    const { data: bundles } = useListBundles(true);
    const bundleByKey = useMemo(()=>new Map((bundles ?? []).map((b)=>[
                b.key,
                b
            ])), [
        bundles
    ]);
    const { data: businessUnitsData } = businessUnitHooks.useList({
        companyId: tenantId
    });
    const businessUnits = businessUnitsData?.items ?? [];
    const businessUnitById = useMemo(()=>new Map(businessUnits.map((b)=>[
                b.id,
                b
            ])), [
        businessUnits
    ]);
    const businessUnitOptions = useMemo(()=>[
            {
                value: ALL_BUSINESS_UNITS,
                label: 'All business units (available everywhere)'
            },
            ...businessUnits.map((b)=>({
                    value: b.id,
                    label: b.name
                }))
        ], [
        businessUnits
    ]);
    const items = roleTypes.data ?? [];
    const [formOpen, setFormOpen] = useState(false);
    // Store the selected id and DERIVE the record from the list (no re-sync effect).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((r)=>r.id === detailId) ?? null : null;
    const [detailMode, setDetailMode] = useState('read');
    const [deleting, setDeleting] = useState(null);
    const invalidate = ()=>{
        void qc.invalidateQueries({
            queryKey: [
                'tenantEnum',
                'role_type'
            ]
        });
        // Same rows, second cache: /admin/permissions' role-names panel reads
        // them under its own key — a registry edit must reach it too.
        void qc.invalidateQueries({
            queryKey: [
                ROLE_NAMES_KEY
            ]
        });
    };
    const upsert = useMutation({
        mutationFn: async (v)=>{
            // The upsert replaces the row, so echo the stored values for fields not
            // edited here. Derive the source row from the target id (empty = create)
            // rather than a separate `editing` state.
            const src = v.id ? items.find((r)=>r.id === v.id) ?? null : null;
            return deliveryClient.upsertTenantEnumValue({
                id: v.id,
                companyId: '',
                domain: 'role_type',
                code: v.code,
                displayLabel: v.displayLabel,
                roleBundleKey: v.roleBundleKey === NO_ACCESS ? '' : v.roleBundleKey,
                selfService: Boolean(v.selfService),
                description: src?.description ?? '',
                sortOrder: src?.sortOrder ?? 0,
                isLeadership: src?.isLeadership ?? false,
                businessUnitId: v.businessUnitId === ALL_BUSINESS_UNITS ? '' : v.businessUnitId
            });
        },
        onSuccess: ()=>{
            void invalidate();
            toast.success('Program role saved');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to save program role')
    });
    const remove = useMutation({
        mutationFn: async (id)=>deliveryClient.deleteRoleType({
                id
            }),
        onSuccess: ()=>{
            void invalidate();
            toast.success('Program role deleted');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to delete program role')
    });
    const renderBusinessUnit = (row)=>row.businessUnitId ? businessUnitById.get(row.businessUnitId)?.name ?? '—' : 'All business units';
    const renderAccess = (row)=>{
        const { bundleKey } = mappingOf(row);
        if (!bundleKey) {
            return /*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground text-xs",
                children: "Roster only — no access"
            });
        }
        const bundle = bundleByKey.get(bundleKey);
        return /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex items-center gap-1.5",
            children: [
                bundle && /*#__PURE__*/ _jsx(ViewModeBadge, {
                    mode: bundle.defaultViewMode
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "text-xs",
                    children: bundle?.displayName ?? bundleKey
                })
            ]
        });
    };
    const columns = [
        {
            key: 'displayLabel',
            header: 'Role'
        },
        {
            key: 'code',
            header: 'Code'
        },
        {
            key: 'businessUnit',
            header: 'Business unit',
            render: renderBusinessUnit
        },
        {
            key: 'access',
            header: 'Grants access as',
            render: renderAccess
        },
        {
            key: 'selfService',
            header: 'Self-service',
            render: (row)=>mappingOf(row).selfService ? 'Yes' : 'No'
        },
        {
            key: 'source',
            header: 'Source',
            render: (row)=>row.isSystem ? 'Built-in' : 'Custom'
        }
    ];
    const bundleOptions = [
        {
            value: NO_ACCESS,
            label: 'No access (roster only)'
        },
        ...(bundles ?? []).map((b)=>({
                value: b.key,
                label: b.displayName || b.key
            }))
    ];
    const fields = [
        {
            kind: 'text',
            name: 'displayLabel',
            label: 'Role name',
            required: true,
            maxLength: 100
        },
        {
            kind: 'text',
            name: 'code',
            label: 'Code (stable identifier)',
            required: true,
            maxLength: 64
        },
        {
            kind: 'select',
            name: 'roleBundleKey',
            label: 'Grants access as',
            required: true,
            options: bundleOptions
        },
        {
            kind: 'select',
            name: 'businessUnitId',
            label: 'Business unit',
            options: businessUnitOptions,
            helpText: 'Leave as "All business units" to make this role name available everywhere; scope it to ' + "narrow it to one business unit's programs.",
            testId: Testids.deliveryRegistries.roleTypeBusinessUnitSelect
        },
        {
            kind: 'checkbox',
            name: 'selfService',
            label: 'Eligible for roster self-service (when the tenant enables it)'
        }
    ];
    // Read sections plus, per row, the inline-edit control — reusing the same
    // field kinds/options as the create form. Source is read-only (built-in vs
    // custom is server-owned, not editable).
    const roleTypeDetailSections = (row)=>{
        const { selfService } = mappingOf(row);
        return [
            {
                rows: [
                    {
                        label: 'Role name',
                        value: row.displayLabel || '—',
                        editField: {
                            kind: 'text',
                            name: 'displayLabel',
                            label: 'Role name',
                            required: true,
                            maxLength: 100
                        }
                    },
                    {
                        label: 'Code',
                        value: row.code || '—',
                        editField: {
                            kind: 'text',
                            name: 'code',
                            label: 'Code (stable identifier)',
                            required: true,
                            maxLength: 64
                        }
                    },
                    {
                        label: 'Grants access as',
                        value: renderAccess(row),
                        editField: {
                            kind: 'select',
                            name: 'roleBundleKey',
                            label: 'Grants access as',
                            required: true,
                            options: bundleOptions
                        }
                    },
                    {
                        label: 'Business unit',
                        value: renderBusinessUnit(row),
                        editField: {
                            kind: 'select',
                            name: 'businessUnitId',
                            label: 'Business unit',
                            options: businessUnitOptions,
                            helpText: 'Leave as "All business units" to make this role name available everywhere; ' + "scope it to narrow it to one business unit's programs.",
                            testId: Testids.deliveryRegistries.roleTypeBusinessUnitSelect
                        }
                    },
                    {
                        label: 'Self-service',
                        value: selfService ? 'Yes' : 'No',
                        editField: {
                            kind: 'checkbox',
                            name: 'selfService',
                            label: 'Eligible for roster self-service (when the tenant enables it)'
                        }
                    },
                    {
                        label: 'Source',
                        value: row.isSystem ? 'Built-in' : 'Custom'
                    }
                ]
            }
        ];
    };
    const submitRoleType = async (values, id)=>{
        await upsert.mutateAsync({
            ...values,
            id
        });
    };
    const detailEdit = detail ? {
        defaultValues: {
            id: detail.id,
            displayLabel: detail.displayLabel ?? '',
            code: detail.code ?? '',
            roleBundleKey: mappingOf(detail).bundleKey || NO_ACCESS,
            selfService: mappingOf(detail).selfService,
            businessUnitId: detail.businessUnitId || ALL_BUSINESS_UNITS
        },
        onSubmit: (v)=>submitRoleType(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-4",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground max-w-2xl text-sm",
                        children: "Roles people hold on a program roster. Each role can grant an access role automatically when someone is assigned it — built-ins are fixed; add your own to match your org chart."
                    }),
                    isAdmin && /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>setFormOpen(true),
                        "data-testid": Testids.deliveryRegistries.roleTypeCreate,
                        children: "New Program Role"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryRegistries.roleTypesTable,
                rowTestid: Testids.deliveryRegistries.roleTypeRow,
                rowKey: (r)=>r.id,
                isLoading: roleTypes.isLoading,
                hideRowActions: !isAdmin,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>row.isSystem ? /*#__PURE__*/ _jsx("span", {
                        className: "text-muted-foreground text-xs",
                        children: "Built-in"
                    }) : /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryRegistries.roleTypeEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryRegistries.roleTypeDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "New Program Role",
                fields: fields,
                defaultValues: {
                    id: '',
                    displayLabel: '',
                    code: '',
                    roleBundleKey: NO_ACCESS,
                    selfService: false,
                    businessUnitId: ALL_BUSINESS_UNITS
                },
                onSubmit: async (v)=>{
                    await submitRoleType(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending,
                submitLabel: "Create"
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.displayLabel || 'Program Role' : '',
                sections: detail ? roleTypeDetailSections(detail) : [],
                testid: Testids.deliveryRegistries.roleTypeDetailDrawer,
                edit: detailEdit,
                // Built-in rows are server-immutable — read-only in the drawer too.
                canEdit: isAdmin && !!detail && !detail.isSystem,
                initialMode: detailMode
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Program Role",
                description: deleting ? `Delete "${deleting.displayLabel}"? Roles in active roster use can't be deleted.` : undefined,
                onConfirm: async ()=>{
                    if (!deleting) return;
                    await remove.mutateAsync(deleting.id);
                    setDeleting(null);
                },
                isPending: remove.isPending
            })
        ]
    });
}
